/**
 * View Transforms AppSync Service
 *
 * Service methods for view transforms operations via AppSync.
 * This file provides a convenience wrapper around the AppSyncService.
 */

import { appSyncService } from "./service";
import type { OnViewTransformUpdateInput, ViewTransform } from "./types";

/**
 * Subscribe to view transform updates
 *
 * Convenience wrapper around appSyncService.subscribeToViewTransformUpdates
 * that provides the same robust error handling, token refresh, and reconnection logic.
 *
 * @param input - Subscription input parameters
 * @param input.organizationId - Organization ID (required)
 * @param onUpdate - Callback function called when a view transform is updated
 * @param onError - Optional callback function called on non-recoverable errors
 * @returns Promise resolving to subscription object with unsubscribe method
 *
 * @example
 * ```typescript
 * const subscription = await subscribeToViewTransformUpdates(
 *   { organizationId: 'org-123' },
 *   (viewTransform) => console.log('View transform updated:', viewTransform),
 *   (error) => console.error('Subscription error:', error)
 * );
 *
 * // Cleanup when done
 * subscription.unsubscribe();
 * ```
 */
export async function subscribeToViewTransformUpdates(
  input: OnViewTransformUpdateInput,
  onUpdate: (viewTransform: ViewTransform) => void,
  onError?: (error: Error) => void
): Promise<{ unsubscribe: () => void }> {
  return appSyncService.subscribeToViewTransformUpdates(input, onUpdate, onError);
}
